// components/FolderContent.jsx
import React, { useState, useEffect, useRef } from 'react';
import authAxios from "../../services/Token"
import { apiImage } from "../../services/Api";

const FolderContent = ({ folder, onUpdate, userId }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [newSubFolderName, setNewSubFolderName] = useState('');
    const [uploading, setUploading] = useState(false);
    const [creatingFolder, setCreatingFolder] = useState(false);
    
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const safeSetState = (setter, value) => {
        if (isMounted.current) {
            setter(value);
        }
    };

    const handleFileUpload = async () => {
        if (!selectedFile) {
            alert('Por favor selecciona un archivo');
            return;
        }

        if (!userId) {
            alert('No se encontró el ID de usuario');
            return;
        }

        safeSetState(setUploading, true);
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('folder_id', folder.id);
        formData.append('user_id', userId);

        try {
            await authAxios.post('documents', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (isMounted.current) {
                setSelectedFile(null);
                onUpdate();
                alert('Archivo subido correctamente');
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            if (isMounted.current) {
                alert('Error al subir el archivo: ' + (error.response?.data?.message || error.message));
            }
        } finally {
            safeSetState(setUploading, false);
        }
    };

    const createSubFolder = async () => {
        if (!newSubFolderName.trim()) {
            alert('Por favor ingresa un nombre para la subcarpeta');
            return;
        }

        if (!userId) {
            alert('No se encontró el ID de usuario');
            return;
        }

        safeSetState(setCreatingFolder, true);
        try {
            await authAxios.post('folders', {
                name: newSubFolderName,
                parent_id: folder.id,
                user_id: userId
            });
            
            if (isMounted.current) {
                setNewSubFolderName('');
                // Llamar a onUpdate que ahora recarga tanto el contenido como el árbol completo
                onUpdate();
                alert('Subcarpeta creada correctamente');
            }
        } catch (error) {
            console.error('Error creating subfolder:', error);
            if (isMounted.current) {
                alert('Error al crear la subcarpeta: ' + (error.response?.data?.message || error.message));
            }
        } finally {
            safeSetState(setCreatingFolder, false);
        }
    };

    const deleteDocument = async (documentId) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar este documento?')) {
            return;
        }
        
        try {
            await authAxios.delete(`documents/${documentId}`);
            if (isMounted.current) {
                onUpdate();
                alert('Documento eliminado correctamente');
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            if (isMounted.current) {
                alert('Error al eliminar el documento: ' + (error.response?.data?.message || error.message));
            }
        }
    };

    const deleteSubFolder = async (subFolderId) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar esta carpeta y todo su contenido?')) {
            return;
        }
        
        try {
            await authAxios.delete(`folders/${subFolderId}`);
            if (isMounted.current) {
                onUpdate();
                alert('Carpeta eliminada correctamente');
            }
        } catch (error) {
            console.error('Error deleting folder:', error);
            if (isMounted.current) {
                alert('Error al eliminar la carpeta: ' + (error.response?.data?.message || error.message));
            }
        }
    };

    const downloadDocument = async (document) => {
    try {
        // Construir la URL completa del archivo usando apiImage + file_path
        const fileUrl = apiImage + document.file_path;
        console.log(fileUrl);
        // Crear un enlace temporal para la descarga
        const link = document.createElement('a');

        link.href = fileUrl;
        link.setAttribute('download', document.original_name);
        link.setAttribute('target', '_blank'); // Opcional: abrir en nueva pestaña
        
        // Agregar el enlace al DOM y simular el click
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
    } catch (error) {
        console.error('Error downloading document:', error);
        if (isMounted.current) {
            alert('Error al descargar el documento: ' + (error.response?.data?.message || error.message));
        }
    }
};

    const getFileIcon = (fileType) => {
        switch(fileType) {
            case 'pdf': return '📄';
            case 'image': return '🖼️';
            case 'excel': return '📊';
            case 'word': return '📝';
            default: return '📎';
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (!isMounted.current) {
        return null;
    }

    return (
        <div>
            {/* Header de la carpeta */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1">📁 {folder.name}</h4>
                    <small className="text-muted">
                        Creado por: <strong>{folder.user?.nombre || 'Usuario'}</strong> - 
                        {new Date(folder.registration_date).toLocaleDateString()}
                    </small>
                </div>
                <div className="text-end">
                    <small className="text-muted d-block">
                        {folder.children?.length || 0} subcarpetas
                    </small>
                    <small className="text-muted d-block">
                        {folder.documents?.length || 0} documentos
                    </small>
                </div>
            </div>

            {/* Panel de acciones */}
            <div className="row mb-4">
                <div className="col-md-6">
                    <div className="card h-100">
                        <div className="card-body">
                            <h5 className="card-title">📂 Crear Subcarpeta</h5>
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Nombre de la subcarpeta"
                                    value={newSubFolderName}
                                    onChange={(e) => setNewSubFolderName(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && createSubFolder()}
                                />
                                <button 
                                    className="btn btn-success"
                                    onClick={createSubFolder}
                                    disabled={!newSubFolderName.trim() || !userId || creatingFolder}
                                >
                                    {creatingFolder ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Creando...
                                        </>
                                    ) : (
                                        'Crear'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-md-6">
                    <div className="card h-100">
                        <div className="card-body">
                            <h5 className="card-title">📤 Subir Archivo</h5>
                            <div className="input-group mb-2">
                                <input
                                    type="file"
                                    className="form-control"
                                    onChange={(e) => setSelectedFile(e.target.files[0])}
                                />
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleFileUpload}
                                    disabled={!selectedFile || uploading || !userId}
                                >
                                    {uploading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Subiendo...
                                        </>
                                    ) : (
                                        'Subir'
                                    )}
                                </button>
                            </div>
                            {selectedFile && (
                                <small className="text-muted">
                                    <strong>Archivo:</strong> {selectedFile.name} 
                                    ({formatFileSize(selectedFile.size)})
                                </small>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Subcarpetas */}
            {folder.children && folder.children.length > 0 && (
                <div className="card mb-4">
                    <div className="bg-primary mb-4">
                        <h5 className="mb-0">📂 Subcarpetas ({folder.children.length})</h5>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            {folder.children.map(child => (
                                <div key={child.id} className="col-md-6 mb-3">
                                    <div className="card border">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                    <h6 className="card-title mb-1">
                                                        📁 {child.name}
                                                    </h6>
                                                    <small className="text-muted d-block">
                                                        <strong>Creado por:</strong> {child.user?.nombre || 'Usuario'}
                                                    </small>
                                                    <small className="text-muted">
                                                        <strong>Fecha:</strong> {new Date(child.registration_date).toLocaleDateString()}
                                                    </small>
                                                </div>
                                                <button 
                                                    className="btn btn-sm btn-outline-danger ms-2"
                                                    onClick={() => deleteSubFolder(child.id)}
                                                    title="Eliminar carpeta"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Documentos */}
            <div className="card">
                <div className=" bg-primary">
                    <h5 className="mb-0">📄 Documentos ({folder.documents ? folder.documents.length : 0})</h5>
                </div>
                <div className="card-body">
                    {folder.documents && folder.documents.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th width="50px">Tipo</th>
                                        <th>Nombre del Archivo</th>
                                        <th width="120px">Tamaño</th>
                                        <th width="120px">Fecha</th>
                                        <th width="150px" className="text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {folder.documents.map(doc => (
                                        <tr key={doc.id}>
                                            <td className="text-center fs-5">
                                                {getFileIcon(doc.file_type)}
                                            </td>
                                            <td>
                                                <div>
                                                    <strong className="d-block">{doc.original_name}</strong>
                                                    <small className="text-muted">
                                                        <span className="badge bg-secondary">
                                                            {doc.file_type.toUpperCase()}
                                                        </span>
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <small className="text-muted">
                                                    {formatFileSize(doc.file_size)}
                                                </small>
                                            </td>
                                            <td>
                                                <small className="text-muted">
                                                    {new Date(doc.created_at).toLocaleDateString()}
                                                </small>
                                            </td>
                                            <td className="text-center">
                                                <div className="btn-group btn-group-sm" role="group">
                                                    <button 
                                                        className="btn btn-outline-success"
                                                        onClick={() => downloadDocument(doc)}
                                                        title="Descargar"
                                                    >
                                                        ⬇️
                                                    </button>
                                                    <button 
                                                        className="btn btn-outline-danger"
                                                        onClick={() => deleteDocument(doc.id)}
                                                        title="Eliminar"
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <div className="fs-1 text-muted mb-3">📄</div>
                            <h5 className="text-muted">No hay documentos en esta carpeta</h5>
                            <p className="text-muted">Sube tu primer archivo usando el formulario superior</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FolderContent;